import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadDocument from '../common/DownloadDocument';
import ExtractionListCompact from './ExtractionListCompact';
import ExtractionList from './ExtractionList';
import SvgEyeIcon from '../../images/icons/eye_icon.svg?react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import CollapsedTitleWithTooltip from '../common/CollapsedTitleWithTooltip';

import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { DocumentDispatchContext } from '../../context/DocumentContext';
import { IDocumentUnscoped } from '../../types/my-types';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LinkTableStyled } from '../common/StyledComponents';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import EditableDocumentDataStatusBadge from './EditableDocumentDataStatusBadge';
import PromoteToParentAction from './PromoteToParentAction';
import {
  IN_LINEAGE_WITH,
  IN_RELATIONSHIP_SET_WITH,
  applyExclusiveGraphFilter,
  getDuplicateLineage,
  getLineageAnchor,
  syncSelectedDocumentParams,
} from './documentUtils';
import { splitCaution } from './split/splitEligibility';
import DocumentDataStatus from '../extractionBatches/details/DocumentDataStatus';
import { DocumentDataStatusFilters } from '../../types/packager-types';
import { formatExclusionReason } from '../../types/packager-enum-descriptions';

export interface DocumentTableRowProps {
  document: IDocumentUnscoped;
}

const DocumentTableRow: React.FC<DocumentTableRowProps> = ({ document }) => {
  const { setToast } = useContext(AppContext);
  const dispatch = useContext<React.Dispatch<any>>(DocumentDispatchContext);
  const [expanded, setExpanded] = useState(false);

  const { dpid } = useParams();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const navigate = useNavigate();

  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const { mutate: generatePage } = useMutation({
    mutationFn: async (id: string) => {
      const response = await (await glynt).document.createPages(id);
      return response;
    },
  });

  const selected = selectedIds.includes(document.id);

  const handleChange = () => {
    const currentParams = new URLSearchParams(window.location.search);
    const currentSelectedIds = currentParams.get('selectedIds')?.split(',') || [];

    let newIds: string[];
    if (currentSelectedIds.includes(document.id)) {
      newIds = currentSelectedIds.filter((id) => id !== document.id);
    } else {
      newIds = [...currentSelectedIds, document.id];
    }

    if (newIds.length === 0) {
      syncSelectedDocumentParams(currentParams, []);
    } else {
      syncSelectedDocumentParams(currentParams, newIds);
    }

    navigate({
      search: currentParams.toString(),
    });
  };

  const btnGeneratePages = async (documentId: string) => {
    await generatePage(documentId);
  };

  const createdDate = new Date(document.created_at);
  const splitRelationship = document.relationships?.split;
  const parentDocument = splitRelationship?.parent;
  const childRelationships = useMemo(
    () => splitRelationship?.children ?? [],
    [splitRelationship?.children]
  );
  const relatedDocumentIds = useMemo(
    () =>
      [parentDocument?.document_id, ...childRelationships.map((child) => child.document_id)].filter(
        (id): id is string => !!id
      ),
    [parentDocument?.document_id, childRelationships]
  );
  const splitWarning = splitCaution(document);

  const { data: relatedDocuments, isFetching: relationshipsLoading } = useQuery({
    queryKey: [
      'document-relationships',
      document.data_pool_id,
      document.id,
      relatedDocumentIds.join(','),
    ],
    queryFn: async () =>
      (await glynt).document.listUnscoped({
        id: relatedDocumentIds.join(','),
        limit: relatedDocumentIds.length,
      }),
    enabled: expanded && relatedDocumentIds.length > 0,
    staleTime: 30_000,
  });

  const relatedDocumentById = useMemo(() => {
    const byId = new Map<string, IDocumentUnscoped>();
    relatedDocuments?.results.forEach((result) => byId.set(result.id, result));
    return byId;
  }, [relatedDocuments]);

  const getRelatedDocument = (documentId: string) => relatedDocumentById.get(documentId);
  const resolvedParentDocument = parentDocument
    ? getRelatedDocument(parentDocument.document_id)
    : undefined;

  const hasDocumentBeenSent = document?.extractions?.find((extraction: any) => extraction.verified);

  // Duplicate lineage from the relationship fields (replaces hash-based grouping).
  const {
    parent: dupParent,
    children: dupChildren,
    isDuplicateChild,
    showDuplicates,
  } = getDuplicateLineage(document);
  const lineage = getLineageAnchor(document);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const iconActionSx = {
    fontSize: '18px',
    color: '#66BBA9',
    cursor: 'pointer',
  };

  const copyDocumentId = (documentId: string) => {
    navigator.clipboard.writeText(documentId);
    setToast({
      open: true,
      message: 'Copied!',
      variant: 'success',
    });
  };

  const copyIconSx = {
    fontSize: '14px',
    cursor: 'pointer',
    color: '#5f6d7e',
  };

  const viewDocument = (relatedDocument?: IDocumentUnscoped) => {
    if (!relatedDocument) return;
    dispatch({
      type: 'TOGGLE_VIEW_DOCUMENT_MODAL',
      pages: relatedDocument.pages,
      id: relatedDocument.id,
    });
  };

  const relationshipTableContainerSx = {
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: '6px',
    overflow: 'hidden',
    backgroundColor: '#fff',
  };

  const relationshipTableSx = {
    minWidth: 0,
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    '& .MuiTableCell-root': {
      px: '10px',
      py: '6px',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      fontSize: '12.5px',
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(102,187,169,0.04)',
    },
  };

  const relationshipTableHeadSx = {
    '& .MuiTableCell-head': {
      backgroundColor: 'rgba(0,0,0,0.015)',
      color: '#6b7280',
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
      py: '5px',
    },
  };

  const relationshipTableColumnSx = {
    name: { width: '50%' },
    id: { width: '15%' },
    pages: { width: '8%' },
    status: { width: '20%' },
    view: { width: '7%' },
  };

  const getDocumentDataStatusReason = (
    relatedDocument?: Pick<
      IDocumentUnscoped,
      'document_data_status' | 'exclusion_reason' | 'escalation_detail'
    >
  ) => {
    if (
      relatedDocument?.document_data_status === DocumentDataStatusFilters.EXCLUDED &&
      relatedDocument.exclusion_reason
    ) {
      return formatExclusionReason(relatedDocument.exclusion_reason);
    }

    if (
      relatedDocument?.document_data_status === DocumentDataStatusFilters.ESCALATED &&
      relatedDocument.escalation_detail
    ) {
      return relatedDocument.escalation_detail;
    }

    return null;
  };

  const renderDocumentDataStatus = (
    relatedDocument?: Pick<
      IDocumentUnscoped,
      'document_data_status' | 'exclusion_reason' | 'escalation_detail'
    >
  ) => {
    const reason = getDocumentDataStatusReason(relatedDocument);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <DocumentDataStatus status={relatedDocument?.document_data_status} />
        {reason && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {reason}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Main Row */}
      <TableRow
        sx={{
          ...(selected && { bgcolor: 'rgba(101,187,169,0.04)' }),
          '& > *': { borderBottom: expanded ? 'none !important' : undefined },
        }}
        data-cy={`document-manifest-row-${document.id}`}
      >
        {/* Expand Button */}
        <TableCell
          align="center"
          sx={{
            width: '40px',
            px: '4px !important',
            borderLeft: selected ? '4px solid #65BBA9' : '4px solid transparent',
          }}
        >
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={handleExpandClick}
            data-cy={`btn-expand-row-${document.id}`}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Checkbox */}
        <TableCell
          align="center"
          sx={{ width: '40px', px: '4px !important', cursor: 'pointer' }}
          onClick={handleChange}
          data-cy={`checkbox-manifest-table-row`}
        >
          <Checkbox
            inputProps={{ 'aria-label': 'secondary checkbox' }}
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={handleChange}
            sx={{ color: '#65BBA9', '&.Mui-checked': { color: '#65BBA9' } }}
          />
        </TableCell>

        {/* ID */}
        <TableCell align="left">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <LinkTableStyled
              to={`/data-pools/${dpid}/documents/${document.id}`}
              data-cy={`link-document-id-${document.id}`}
            >
              <CollapsedTitleWithTooltip title={document.id} charsFirstAndLast={4} clickToCopy />
            </LinkTableStyled>
            <span
              data-cy={`copy-document-id-${document.id}`}
              style={{ display: 'flex', alignItems: 'center' }}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(document.id);
                setToast({
                  open: true,
                  message: 'Copied!',
                  variant: 'success',
                });
              }}
            >
              <ContentCopyIcon style={{ fontSize: '14px', cursor: 'pointer', color: '#5f6d7e' }} />
            </span>
          </Box>
        </TableCell>

        {/* Parent */}
        <TableCell align="left">
          {parentDocument ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LinkTableStyled
                to={`/data-pools/${parentDocument.data_pool_id}/documents/${parentDocument.document_id}`}
                data-cy={`link-parent-document-${document.id}`}
              >
                <CollapsedTitleWithTooltip
                  title={parentDocument.document_id}
                  charsFirstAndLast={4}
                />
              </LinkTableStyled>
              <Tooltip title="Copy parent ID" placement="top">
                <ContentCopyIcon
                  data-cy={`copy-parent-document-id-${document.id}`}
                  sx={{ fontSize: '14px', cursor: 'pointer', color: '#5f6d7e' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    copyDocumentId(parentDocument.document_id);
                  }}
                />
              </Tooltip>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No
            </Typography>
          )}
        </TableCell>

        {/* Children */}
        <TableCell align="center">
          {childRelationships.length > 0 ? (
            <Box
              component="button"
              type="button"
              data-cy={`btn-child-count-${document.id}`}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded(true);
              }}
              sx={{
                p: 0,
                border: 0,
                background: 'none',
                color: '#187787',
                font: 'inherit',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {childRelationships.length}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              0
            </Typography>
          )}
        </TableCell>

        {/* Vendor */}
        <TableCell align="left" sx={{ color: '#5f6d7e' }}>
          {document.vendor_name || '—'}
        </TableCell>

        {/* Name */}
        <TableCell align="left" sx={{ maxWidth: '250px' }}>
          <Tooltip
            placement="top"
            title={document.label ? document.label : ''}
            disableFocusListener
            disableTouchListener
          >
            <Box
              component="span"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigator.clipboard.writeText(document.label);
                setToast({
                  open: true,
                  message: 'Copied!',
                  variant: 'success',
                });
              }}
            >
              {document.label}
              {document.file_extension || ''}
            </Box>
          </Tooltip>
        </TableCell>

        {/* Date */}
        <TableCell align="center" sx={{ minWidth: '100px', color: '#5f6d7e' }}>
          <Tooltip
            placement="top"
            title={createdDate.toString()}
            disableFocusListener
            disableTouchListener
          >
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(createdDate.toString());
                setToast({
                  open: true,
                  message: 'Copied!',
                  variant: 'success',
                });
              }}
            >
              {createdDate.toLocaleDateString()}
            </span>
          </Tooltip>
        </TableCell>

        {/* Pages */}
        <TableCell align="center" sx={{ width: '60px', color: '#5f6d7e' }}>
          <span data-cy={`lbl-document-page-count-${document.id}`}>{document.pages.length}</span>
        </TableCell>

        {/* Data Status */}
        <TableCell align="center" sx={{ width: '170px', maxWidth: '170px' }}>
          <EditableDocumentDataStatusBadge
            documentId={document.id}
            documentDataId={document.document_data_id}
            status={document.document_data_status}
            exclusionReason={document.exclusion_reason}
            escalationDetail={document.escalation_detail}
          />
        </TableCell>

        {/* Info */}
        <TableCell align="left" sx={{ px: '8px !important', minWidth: '330px', width: '330px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '72px 90px 76px 80px',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {/* Batches column */}
            <Box>
              {document?.extractions && document.extractions.length > 0 ? (
                <ExtractionListCompact
                  dataPoolId={document.data_pool_id}
                  extractions={document.extractions}
                  document={document}
                />
              ) : null}
            </Box>

            {/* Dupes column */}
            <Box>
              {showDuplicates && (
                <Box
                  data-cy="btn-duplicate-count"
                  onClick={() => {
                    // A Document id here; the lineage chip below takes a DocumentData id.
                    applyExclusiveGraphFilter(searchParams, IN_RELATIONSHIP_SET_WITH, document.id);
                    navigate({ search: searchParams.toString() });
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: '20px',
                    px: '8px',
                    fontSize: '11px',
                    fontWeight: 500,
                    borderRadius: '10px',
                    border: '1px solid rgba(0,0,0,0.2)',
                    color: '#5f6d7e',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                  }}
                >
                  {dupChildren.length > 0
                    ? `${dupChildren.length} duplicate${dupChildren.length !== 1 ? 's' : ''}`
                    : 'duplicate'}
                </Box>
              )}
            </Box>

            {/* Lineage column — origin drill-down */}
            <Box>
              {lineage && (
                <Tooltip
                  placement="top"
                  title={
                    lineage.isCrossPool
                      ? 'Origin is in another data pool; this list only shows documents in the current pool.'
                      : lineage.isOrigin
                        ? 'This document is the origin. Show everything split from it.'
                        : 'Show every document from the same original file.'
                  }
                  disableFocusListener
                  disableTouchListener
                >
                  <Box
                    data-cy="btn-lineage-origin"
                    onClick={() => {
                      // A DocumentData id, NOT a Document id. The duplicate badge above
                      // takes the opposite species and the API answers 200 either way, so
                      // mixing them fails silently.
                      applyExclusiveGraphFilter(
                        searchParams,
                        IN_LINEAGE_WITH,
                        lineage.anchorDocumentDataId
                      );
                      navigate({ search: searchParams.toString() });
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '20px',
                      px: '8px',
                      fontSize: '11px',
                      fontWeight: 500,
                      borderRadius: '10px',
                      // Deliberately NOT the dupe badge's grey: two identical pills 4px
                      // apart firing different graph filters reads as interchangeable.
                      border: '1px solid #7E9CC7',
                      color: '#3E5C86',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      '&:hover': { backgroundColor: 'rgba(62,92,134,0.06)' },
                    }}
                  >
                    {lineage.isOrigin ? 'origin' : lineage.isCrossPool ? 'lineage ↗' : 'lineage'}
                  </Box>
                </Tooltip>
              )}
            </Box>

            {/* Status column (Sent / Unsupported) */}
            <Box>
              {hasDocumentBeenSent ? (
                <Tooltip
                  placement="top"
                  title={`Sent on ${new Date(hasDocumentBeenSent.created_at)}`}
                  disableFocusListener
                  disableTouchListener
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '20px',
                      px: '8px',
                      fontSize: '11px',
                      fontWeight: 500,
                      borderRadius: '10px',
                      backgroundColor: '#66BBA9',
                      color: 'white',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Sent
                  </Box>
                </Tooltip>
              ) : !document.is_supported ? (
                <Tooltip
                  placement="top"
                  title="Document Not Supported"
                  disableFocusListener
                  disableTouchListener
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '20px',
                      px: '8px',
                      fontSize: '11px',
                      fontWeight: 500,
                      borderRadius: '10px',
                      border: '1px solid #F44336',
                      color: '#F44336',
                      backgroundColor: 'white',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Unsupported
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        </TableCell>

        {/* Actions */}
        <TableCell
          align="right"
          sx={{
            minWidth: '190px',
            width: '190px',
            position: 'sticky',
            right: 0,
            zIndex: 1,
            backgroundColor: selected ? '#f7fbfa' : '#fff',
            boxShadow: '-6px 0 8px -8px rgba(0,0,0,0.2)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '10px',
              whiteSpace: 'nowrap',
            }}
          >
            {isDuplicateChild && document.document_data_id && (
              <PromoteToParentAction
                documentId={document.id}
                documentDataId={document.document_data_id}
              />
            )}
            <Tooltip
              placement="top"
              title={splitWarning ?? 'Split document'}
              disableFocusListener
              disableTouchListener
            >
              <span>
                <IconButton
                  size="small"
                  data-cy={`btn-split-document-${document.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/data-pools/${document.data_pool_id}/documents/${document.id}/split`);
                  }}
                  sx={{ p: 0, color: '#66BBA9' }}
                >
                  <CallSplitIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              placement="top"
              title={!document.is_supported ? 'Document Not Supported' : 'View Document'}
              disableFocusListener
              disableTouchListener
            >
              <Box component="span">
                <SvgEyeIcon
                  data-cy="icon-view-document"
                  style={{
                    maxHeight: '18px',
                    color: '#66BBA9',
                    cursor: 'pointer',
                    opacity: !document.is_supported ? 0.4 : 1,
                    pointerEvents: !document.is_supported ? 'none' : 'auto',
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    dispatch({
                      type: 'TOGGLE_VIEW_DOCUMENT_MODAL',
                      pages: document.pages,
                      id: document.id,
                    });
                  }}
                />
              </Box>
            </Tooltip>

            <DownloadDocument
              documentId={document.id}
              documentContentType={document.content_type}
              documentLabel={document.label}
              documentFileExtension={document.file_extension}
              iconType="file"
            />

            <Tooltip
              placement="top"
              title="Exclude Document"
              disableFocusListener
              disableTouchListener
            >
              <CancelIcon
                sx={iconActionSx}
                data-cy="icon-exclude-document"
                onClick={(event) => {
                  event.stopPropagation();
                  const excludeIds = searchParams.get('exclude_id')?.split(',') || [];
                  if (excludeIds.includes(document.id)) {
                    return;
                  }
                  excludeIds.push(document.id);
                  searchParams.set('exclude_id', excludeIds.join(','));
                  navigate({
                    search: searchParams.toString(),
                  });
                }}
              />
            </Tooltip>

            {document.pages.length === 0 && (
              <Tooltip
                placement="top"
                title={!document.is_supported ? 'Document Not Supported' : 'Generate Pages'}
                disableFocusListener
                disableTouchListener
              >
                <AssignmentIcon
                  sx={{
                    ...iconActionSx,
                    opacity: !document.is_supported ? 0.4 : 1,
                    pointerEvents: !document.is_supported ? 'none' : 'auto',
                  }}
                  onClick={() => {
                    btnGeneratePages(document.id);
                    queryClient.invalidateQueries({
                      queryKey: ['documents'],
                    });
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
      {/* Expanded Details Row */}
      <TableRow sx={{ bgcolor: '#f9fafb', '&:hover': { bgcolor: '#f9fafb !important' } }}>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            borderBottom: expanded ? '1px solid rgba(0,0,0,0.08)' : 'none',
          }}
          colSpan={12}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                py: 1.5,
                px: 2,
                backgroundColor: '#f9fafb',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}>
                {/* Format */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: 'bold', mr: 0.5 }}
                  >
                    Format:
                  </Typography>
                  {document.format_id ? (
                    <>
                      <CollapsedTitleWithTooltip
                        title={document.format_id}
                        charsFirstAndLast={6}
                        clickToCopy
                      />
                      <Tooltip title="Filter by format" placement="top">
                        <FilterAltIcon
                          sx={{ fontSize: '14px', cursor: 'pointer', color: '#5f6d7e' }}
                          onClick={() => {
                            searchParams.set('page', '0');
                            searchParams.set('format_id', document.format_id || '');
                            navigate({ search: searchParams.toString() });
                          }}
                        />
                      </Tooltip>
                    </>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </Box>

                {/* Language */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: 'bold', mr: 0.5 }}
                  >
                    Language:
                  </Typography>
                  <Typography variant="body2">
                    {document.language ? document.language.toUpperCase() : '—'}
                  </Typography>
                </Box>

                {/* Extraction Batches - Full List */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: 'bold', mr: 0.5, whiteSpace: 'nowrap' }}
                  >
                    Batches:
                  </Typography>
                  {document?.extractions && document.extractions.length > 0 ? (
                    <ExtractionList
                      dataPoolId={document.data_pool_id}
                      extractions={document.extractions}
                      document={document}
                    />
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </Box>
              </Box>

              {parentDocument && (
                <Box sx={{ width: '100%' }} data-cy={`document-parent-${document.id}`}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', fontWeight: 'bold', mb: 0.75 }}
                  >
                    Parent:
                  </Typography>
                  <Box sx={{ ...relationshipTableContainerSx, maxWidth: '980px' }}>
                    <Table sx={relationshipTableSx}>
                      <TableHead sx={relationshipTableHeadSx}>
                        <TableRow>
                          <TableCell sx={relationshipTableColumnSx.name}>Name</TableCell>
                          <TableCell sx={relationshipTableColumnSx.id}>ID</TableCell>
                          <TableCell align="right" sx={relationshipTableColumnSx.pages}>
                            Pages
                          </TableCell>
                          <TableCell sx={relationshipTableColumnSx.status}>Data Status</TableCell>
                          <TableCell align="right" sx={relationshipTableColumnSx.view}>
                            View
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relationshipsLoading && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <LinearProgress />
                            </TableCell>
                          </TableRow>
                        )}
                        {!relationshipsLoading && (
                          <TableRow>
                            <TableCell>
                              <LinkTableStyled
                                to={`/data-pools/${parentDocument.data_pool_id}/documents/${parentDocument.document_id}`}
                                data-cy={`link-expanded-parent-document-${parentDocument.document_id}`}
                                style={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {resolvedParentDocument
                                  ? `${resolvedParentDocument.label}${resolvedParentDocument.file_extension || ''}`
                                  : parentDocument.document_id}
                              </LinkTableStyled>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{parentDocument.document_id}</span>
                                <ContentCopyIcon
                                  sx={copyIconSx}
                                  data-cy={`copy-expanded-parent-document-id-${parentDocument.document_id}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    copyDocumentId(parentDocument.document_id);
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {resolvedParentDocument?.pages.length ?? '—'}
                            </TableCell>
                            <TableCell>
                              {renderDocumentDataStatus(resolvedParentDocument)}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip
                                title={
                                  resolvedParentDocument ? 'View Document' : 'Document unavailable'
                                }
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <SvgEyeIcon
                                    data-cy={`icon-view-expanded-parent-document-${parentDocument.document_id}`}
                                    style={{
                                      maxHeight: '16px',
                                      color: '#66BBA9',
                                      cursor: resolvedParentDocument ? 'pointer' : 'default',
                                      opacity: resolvedParentDocument ? 1 : 0.35,
                                      pointerEvents: resolvedParentDocument ? 'auto' : 'none',
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      viewDocument(resolvedParentDocument);
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}

              {childRelationships.length > 0 && (
                <Box sx={{ width: '100%' }} data-cy={`document-children-${document.id}`}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', fontWeight: 'bold', mb: 0.75 }}
                  >
                    Children ({childRelationships.length}):
                  </Typography>
                  <Box sx={{ ...relationshipTableContainerSx, maxWidth: '980px' }}>
                    <Table sx={relationshipTableSx}>
                      <TableHead sx={relationshipTableHeadSx}>
                        <TableRow>
                          <TableCell sx={relationshipTableColumnSx.name}>Name</TableCell>
                          <TableCell sx={relationshipTableColumnSx.id}>ID</TableCell>
                          <TableCell align="right" sx={relationshipTableColumnSx.pages}>
                            Pages
                          </TableCell>
                          <TableCell sx={relationshipTableColumnSx.status}>Data Status</TableCell>
                          <TableCell align="right" sx={relationshipTableColumnSx.view}>
                            View
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relationshipsLoading && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <LinearProgress />
                            </TableCell>
                          </TableRow>
                        )}
                        {!relationshipsLoading &&
                          childRelationships.map((child) => {
                            const childDocument = getRelatedDocument(child.document_id);

                            return (
                              <TableRow key={child.document_id}>
                                <TableCell>
                                  <LinkTableStyled
                                    to={`/data-pools/${child.data_pool_id}/documents/${child.document_id}`}
                                    data-cy={`link-child-document-${child.document_id}`}
                                    style={{
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {childDocument
                                      ? `${childDocument.label}${childDocument.file_extension || ''}`
                                      : child.document_id}
                                  </LinkTableStyled>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>{child.document_id}</span>
                                    <ContentCopyIcon
                                      sx={copyIconSx}
                                      data-cy={`copy-expanded-child-document-id-${child.document_id}`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        copyDocumentId(child.document_id);
                                      }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  {childDocument?.pages.length ?? '—'}
                                </TableCell>
                                <TableCell>{renderDocumentDataStatus(childDocument)}</TableCell>
                                <TableCell align="right">
                                  <Tooltip
                                    title={childDocument ? 'View Document' : 'Document unavailable'}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                      <SvgEyeIcon
                                        data-cy={`icon-view-expanded-child-document-${child.document_id}`}
                                        style={{
                                          maxHeight: '16px',
                                          color: '#66BBA9',
                                          cursor: childDocument ? 'pointer' : 'default',
                                          opacity: childDocument ? 1 : 0.35,
                                          pointerEvents: childDocument ? 'auto' : 'none',
                                        }}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          viewDocument(childDocument);
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Duplicate lineage (parent / children) */}
            {showDuplicates && (
              <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dupParent && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                      Duplicate of:
                    </Typography>
                    <LinkTableStyled
                      to={`/data-pools/${dupParent.data_pool_id}/documents/${dupParent.document_id}`}
                      data-cy={`link-duplicate-parent-${document.id}`}
                    >
                      <CollapsedTitleWithTooltip
                        title={dupParent.document_id}
                        charsFirstAndLast={4}
                      />
                    </LinkTableStyled>
                    <Typography variant="caption" color="textSecondary">
                      ({dupParent.detection_method})
                    </Typography>
                  </Box>
                )}
                {dupChildren.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                      Duplicates ({dupChildren.length}):
                    </Typography>
                    {dupChildren.map((child) => (
                      <LinkTableStyled
                        key={child.document_id}
                        to={`/data-pools/${child.data_pool_id}/documents/${child.document_id}`}
                        data-cy={`link-duplicate-child-${child.document_id}`}
                      >
                        <CollapsedTitleWithTooltip
                          title={child.document_id}
                          charsFirstAndLast={4}
                        />
                      </LinkTableStyled>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default DocumentTableRow;
